import type { JSONSchema7 } from 'json-schema'
import { z } from 'zod'

import { INSTALLATION_DATE_REGEX } from './installationDateHelpers'

export const COORDINATE_PAIR_REGEX =
  /\[\s*-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?\s*\]/g

export const optionalNumberSchema = z
  .union([
    z.literal('').transform(() => undefined),
    z.coerce.number<number>({ message: 'Must be a number input' }),
  ])
  .optional()

export const requiredNumberSchema = z.union([
  z
    .string()
    .refine((val) => val.trim() !== '' && !Number.isNaN(Number(val.trim())), {
      message: 'A number is required',
    })
    .transform((val) => Number(val.trim())),
  z.number({ message: 'A number is required' }),
])

export const requiredIntSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        // Convert blank strings to undefined to trigger validation error
        return undefined
      }
      const parsedNumber = Number(trimmedInput)
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber // Return the parsed number if valid
      }
    }
    return input // Return the input as-is for further validation if not a string
  },
  z
    .number({
      error: 'A number is required',
    })
    .int({
      message: 'Must be an integer input',
    })
)

export const positiveNumberSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        // Convert blank strings to undefined to trigger validation error
        return undefined
      }
      const parsedNumber = Number(trimmedInput)
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber // Return the parsed number if valid
      }
    }
    return input // Return the input as-is for further validation if not a string
  },
  z
    .number({
      error: 'A number is required',
    })
    .gt(0, { message: 'Number must be greater than 0' })
)

export const optionalDateStringWithRegex = z.preprocess((input) => {
  if (typeof input === 'string') {
    const trimmedInput = input.trim()
    if (trimmedInput === '') {
      return undefined // Convert blank strings to undefined
    }
  }
  return input // Return the input for further validation if not blank
}, z.string().regex(INSTALLATION_DATE_REGEX, 'Invalid date format, must be YYYY-MM-DD').optional()) // Validate with regex and allow undefined

export const requiredDateStringWithRegex = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        return undefined // Convert blank strings to undefined
      }
    }
    return input // Return the input for further validation if not blank
  },
  z
    .string()
    .regex(INSTALLATION_DATE_REGEX, 'Date is required, must be YYYY-MM-DD')
) // Validate with regex and allow undefined

export const optionalStringSchema = z
  .union([
    z.null().transform(() => undefined),
    z.string().transform((val) => val.trim()),
    z.number().transform(() => undefined),
  ])
  .optional()

export const requiredStringSchema = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        return undefined // Convert blank strings to undefined
      }
      return trimmedInput // Return the trimmed input for further validation if not blank
    }
    return input
  },
  z
    .string({ error: 'A string is required' })
    .min(1, 'The string must not be empty')
) // Ensure not empty and allow undefined

export const optionalGeckoAddressSchema = z.preprocess(
  (input) => {
    if (typeof input === 'object') {
      return input
    }
    return undefined
  },
  z
    .object({
      address_line_1: z.string(),
      address_line_2: z.string().optional(),
      city: z.string(),
      region: z.string(),
      country: z.string(),
      postal_code: z.string(),
    })
    .optional()
)

export const requiredCoordinatesArraySchema = (minCoordinates: number = 3) =>
  z
    .string({
      error: 'A string of coordinates is required',
    })
    .superRefine((input, context) => {
      // Match and extract all the coordinate pairs using regex
      const matches = input.match(COORDINATE_PAIR_REGEX)

      if (!matches) {
        // If no matches, the format is incorrect
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Invalid coordinate pair format, must be [x, y], [x, y], ...',
        })
        return
      }

      if (matches.length < minCoordinates) {
        // If not enough coordinate pairs
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Must contain at least ${minCoordinates} coordinate pairs`,
        })
      }
    })

export const requiredDropdown = z.preprocess(
  (input) => {
    return input
  },
  z.string().array().nonempty({ message: 'An option is required' })
)

export const optionalDropdown = z.preprocess((input) => {
  return input
}, z.string().array().optional())

export const requiredConeAngle = z.preprocess(
  (input) => {
    if (typeof input === 'string') {
      const trimmedInput = input.trim()
      if (trimmedInput === '') {
        return undefined
      }
      const parsedNumber = Number(trimmedInput)
      if (!Number.isNaN(parsedNumber)) {
        return parsedNumber
      }
    }
    return input
  },
  z
    .number({
      error: 'A number is required',
    })
    .refine((num) => num > 0 && num < 90, {
      message: 'Angle must be greater than 0 and less than 90',
    })
)

// JSON Schema utilities
/**
 * Supported field types for JSON schema generation
 */
export enum SchemaFieldType {
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  ENUM = 'enum',
}

/**
 * Form data structure for schema field definition
 */
export interface SchemaField {
  key: string
  type: SchemaFieldType
  required?: boolean
  enumOptions?: string[] // Only used when type is ENUM
  description?: string
}

/**
 * Convert a simple schema field definition to JSONSchema7 property
 */
export function schemaFieldToJsonSchemaProperty(
  field: SchemaField
): JSONSchema7 {
  switch (field.type) {
    case SchemaFieldType.STRING:
      return {
        type: 'string',
        description: field.description,
      }
    case SchemaFieldType.NUMBER:
      return {
        type: 'number',
        description: field.description,
      }
    case SchemaFieldType.BOOLEAN:
      return {
        type: 'boolean',
        description: field.description,
      }
    case SchemaFieldType.ENUM:
      return {
        type: 'string',
        enum: field.enumOptions || [],
        description: field.description,
      }
    default:
      return {
        type: 'string',
        description: field.description,
      }
  }
}

/**
 * Convert array of schema fields to JSONSchema7
 */
export function schemaFieldsToJsonSchema(fields: SchemaField[]): JSONSchema7 {
  const properties: Record<string, JSONSchema7> = {}
  const required: string[] = []

  fields.forEach((field) => {
    if (field.key.trim()) {
      properties[field.key] = schemaFieldToJsonSchemaProperty(field)
      if (field.required) {
        required.push(field.key)
      }
    }
  })

  return {
    type: 'object',
    properties,
    required: required.length > 0 ? required : undefined,
    additionalProperties: false,
  }
}

/**
 * Convert JSONSchema7 back to schema fields for form editing
 */
export function jsonSchemaToSchemaFields(schema: JSONSchema7): SchemaField[] {
  if (!schema.properties || typeof schema.properties !== 'object') {
    return []
  }

  const requiredFields = Array.isArray(schema.required) ? schema.required : []

  return Object.entries(schema.properties).map(([key, property]) => {
    const prop = property as JSONSchema7
    let type = SchemaFieldType.STRING
    let enumOptions: string[] | undefined

    if (prop.type === 'number') {
      type = SchemaFieldType.NUMBER
    } else if (prop.type === 'boolean') {
      type = SchemaFieldType.BOOLEAN
    } else if (prop.type === 'string' && Array.isArray(prop.enum)) {
      type = SchemaFieldType.ENUM
      enumOptions = prop.enum as string[]
    }

    return {
      key,
      type,
      required: requiredFields.includes(key),
      enumOptions,
      description:
        typeof prop.description === 'string' ? prop.description : undefined,
    }
  })
}

/**
 * Zod schema for validating schema field input
 */
export const schemaFieldZodSchema = z.object({
  key: z.string().min(1, 'Field key is required'),
  type: z.nativeEnum(SchemaFieldType),
  required: z.boolean().optional(),
  enumOptions: z.array(z.string()).optional(),
  description: z.string().optional(),
})

export const schemaFieldsZodSchema = z.array(schemaFieldZodSchema)
